import React, { useState, useEffect, useRef } from "react";
import CustomModal from "../../../Core/CommonV2/CustomModal";
import CustomButton from "../../../Core/CommonV2/CustomButton";
import { useAppDispatch, useAppSelector } from "../../../Core/redux/hooks";
import {
  getAllFiles,
  getAllFolders,
  getAllLinks,
} from "../../../Core/redux/api/vaultAPI";
import { useNavigate, useParams } from "react-router-dom";
import { client } from "../../../Core/utils/axiosClient";
import { updateDashboard } from "../../../Core/redux/slices/dashboard.slice";
import CustomInputBox from "../../../Core/CommonV2/CustomInputBox";
import { CheckIcon } from "../../../Core/svgV2/Check";
import PhaseIcon from "../../../Core/svg/PhaseIcon";
import CustomAvatar from "../../../Core/CommonV2/CustomAvatar";
import { Dropdown, Spin, Tooltip } from "antd";
import ConfirmMoveModal from "./ConfirmMoveModal";

const MoveToModal = ({ visible, onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { projectId, "*": splat } = useParams();
  const { tenantDetails } = useAppSelector((state) => state.tenant);
  const { projectList } = useAppSelector((state) => state.projects);
  const { moveToFolderModal, currentSelectedProject, folders, files, links } =
    useAppSelector((state) => state.vault);

  const [moveToFolderId, setMoveToFolderId] = useState(null);
  const [moveToProjectId, setMoveToProjectId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"projects" | "folders">("folders");

  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [currentFolders, setCurrentFolders] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [confirmVisible, setConfirmVisible] = useState(false);

  const [targetName, setTargetName] = useState("");
  const [breadcrumbDropdownVisible, setBreadcrumbDropdownVisible] =
    useState(false);

  const [isFoldersLoading, setIsFoldersLoading] = useState(false);

  // const lastClickTimeRef = useRef(0);

  const getCurrentFolderContext = () => {
    if (!splat) return null;

    const pathSnippets = splat.split("/").filter((i) => i);
    if (pathSnippets.length === 0) return null;

    return pathSnippets.map((segment) => {
      const parts = segment.split("-");
      const name = parts.slice(0, -1).join("-");
      const id = parts[parts.length - 1];
      return { id, folderName: name };
    });
  };

  const resetModalState = () => {
    setMoveToFolderId(null);
    setMoveToProjectId(null);
    setCurrentFolderId(null);
    setFolderStack([]);
    setCurrentFolders([]);
    setSearch("");
    setTargetName("");
    setIsFoldersLoading(false);
  };

  const initializeModalContext = () => {
    const folderContext = getCurrentFolderContext();

    if (projectId) {
      // user is in a project context
      setMoveToProjectId(projectId);
      setViewMode("folders");

      if (folderContext?.length > 0) {
        // user is inside folders - setting the folder stack to current path
        setFolderStack(folderContext);
        setCurrentFolderId(folderContext[folderContext.length - 1].id);
      } else {
        // user is at project root
        setFolderStack([]);
        setCurrentFolderId(null);
      }
    } else {
      // user is in global library view
      setMoveToProjectId(null);
      setViewMode("projects");
      setFolderStack([]);
      setCurrentFolderId(null);
    }
  };

  const handleMoveClick = () => {
    setConfirmVisible(true);
  };

  // curr project name
  const getCurrentProjectName = () => {
    if (projectId) {
      if (currentSelectedProject?.title) {
        return currentSelectedProject.title;
      }

      const project = projectList.find((p) => p.id === projectId);
      if (project?.title) {
        return project.title;
      }

      return `Project ${projectId}`;
    }
    return "Library";
  };

  useEffect(() => {
    if (viewMode === "projects") {
      const project = projectList.find((p) => p.id === moveToProjectId);
      setTargetName(project?.title || "Selected Project");
    } else {
      // folders view
      if (moveToFolderId === null) {
        const project = projectList.find((p) => p.id === moveToProjectId);
        setTargetName(project?.title || getCurrentProjectName());
      } else {
        const folderFromStack = folderStack.find(
          (f) => f.id === moveToFolderId
        );
        if (folderFromStack) {
          setTargetName(folderFromStack.folderName);
        } else {
          const folder = currentFolders.find((f) => f.id === moveToFolderId);
          setTargetName(folder?.folderName || "Selected Folder");
        }
      }
    }
  }, [
    viewMode,
    moveToFolderId,
    moveToProjectId,
    folderStack,
    currentFolders,
    projectList,
  ]);

  const itemName = () => {
    const { docId, docType } = moveToFolderModal;

    switch (docType) {
      case "folder": {
        const folder = folders.find((f) => f.id === docId);
        return `'${folder?.folderName}'` || "Folder";
      }

      case "file": {
        const file = files.find((f) => f.id === docId);
        return `'${file?.name}'` || "File";
      }
      case "link": {
        const link = links.find((l) => l.id === docId);
        return `'${link?.name}'` || "Link";
      }
      default:
        return "Item";
    }
  };

  const loadFolders = async (folderId = null) => {
    try {
      setIsFoldersLoading(true);

      const response = await client.get("/repository/folder", {
        params: {
          projectId: moveToProjectId || projectId,
          tenantId: tenantDetails?.tenantId,
          folderId,
        },
      });

      if (response?.data?.result) {
        setCurrentFolders(response.data.result);
      } else {
        setCurrentFolders([]);
      }
    } catch (err) {
      console.error("Error fetching folders", err);
      setCurrentFolders([]);
    } finally {
      setIsFoldersLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      resetModalState();
      initializeModalContext();
    }
  }, [visible, projectId, splat]);

  // when project is selected, load its folders
  useEffect(() => {
    if (visible && moveToProjectId) {
      loadFolders(currentFolderId);
    }
  }, [moveToProjectId, currentFolderId, visible]);

  const documentsAfterMove = () => {
    let query: { projectId?: string; folderId?: string; tenantId?: string } =
      {};

    if (!projectId || projectId === "undefined") {
      query = { projectId: null, tenantId: tenantDetails?.tenantId };
    } else {
      query = { projectId };

      if (splat?.length > 0) {
        const pathSnippets = splat.split("/").filter((i) => i);
        if (pathSnippets.length > 0) {
          const lastSegment = pathSnippets[pathSnippets.length - 1];
          const parts = lastSegment.split("-");
          const currentFolderId = parts[parts.length - 1];

          if (currentFolderId) {
            query.folderId = currentFolderId;
          }
        }
      }
    }

    return query;
  };

  const isDescendantOrSelf = (targetFolderId, currentFolderId) => {
    if (targetFolderId === currentFolderId) return true;
    return targetFolderId.toString() === currentFolderId.toString();
  };

  const handleFolderClick = (folder) => {
    const now = Date.now();

    // if (now - lastClickTimeRef.current < 300) {
    //   return;
    // }

    // lastClickTimeRef.current = now;

    if (isFoldersLoading) {
      return;
    }

    setFolderStack((prev) => {
      const existingIndex = prev.findIndex((f) => f.id === folder.id);
      if (existingIndex !== -1) {
        return prev.slice(0, existingIndex + 1);
      }
      return [...prev, { id: folder.id, folderName: folder.folderName }];
    });
    setMoveToFolderId(folder.id);
    setCurrentFolderId(folder.id);
    loadFolders(folder.id);
  };

  const onsubmit = async () => {
    // if we r in library screen (projects viiew) view and no project is selected
    if (viewMode === "projects" && !moveToProjectId) {
      dispatch(
        updateDashboard({
          key: "alertPopupModal",
          value: {
            visible: true,
            data: {
              title: "Selection required",
              subtitle: "Please select a destination project",
              description: "",
            },
          },
        })
      );
      return;
    }

    // if we r in folders view and undefined folder ID (distinct from null which is root)
    if (viewMode === "folders" && moveToFolderId === undefined) {
      dispatch(
        updateDashboard({
          key: "alertPopupModal",
          value: {
            visible: true,
            data: {
              title: "Selection required",
              subtitle: "Please select a destination folder",
              description: "",
            },
          },
        })
      );
      return;
    }

    //here we only the descendant chk if moving to folders and not root(when movetofolder is null)
    if (
      viewMode === "folders" &&
      moveToFolderModal.docType === "folder" &&
      moveToFolderId &&
      isDescendantOrSelf(moveToFolderModal.docId, moveToFolderId)
    ) {
      dispatch(
        updateDashboard({
          key: "alertPopupModal",
          value: {
            visible: true,
            data: {
              title: "Invalid Selection",
              subtitle: "Cannot move a folder into itself or its descendant",
              description: "",
            },
          },
        })
      );
      return;
    }

    setIsLoading(true);

    const payload = {
      itemId: moveToFolderModal.docId,
      itemType: moveToFolderModal.docType,
      folderId: moveToFolderId || null,
      projectId: moveToProjectId || projectId,
    };

    try {
      await client.put(`/repository/move-item-to-folder`, payload);

      let destinationName = "root folder";
      if (moveToProjectId) {
        const project = projectList.find((p) => p.id === moveToProjectId);
        destinationName = `${project?.title || "Selected project"}`;
      } else if (moveToFolderId) {
        const folder = currentFolders.find((f) => f.id === moveToFolderId);
        destinationName = `${folder?.folderName || "Selected folder"}`;
      } else if (projectId) {
        destinationName = `${getCurrentProjectName()}`;
      }

      const currentItemName = itemName();

      dispatch(
        updateDashboard({
          key: "alertPopupModal",
          value: {
            visible: true,
            data: {
              title: "Location updated",
              subtitle: `${currentItemName} moved to `,
              description: targetName,
            },
          },
        })
      );

      const queryObject = documentsAfterMove();

      if (moveToFolderModal.docType === "folder") {
        await dispatch(getAllFolders(queryObject));
      } else if (moveToFolderModal.docType === "file") {
        await dispatch(getAllFiles(queryObject));
      } else {
        await dispatch(getAllLinks(queryObject));
      }

      onClose();
    } catch (error) {
      console.error("Error moving item:", error);
      dispatch(
        updateDashboard({
          key: "alertPopupModal",
          value: {
            visible: true,
            data: {
              title: "Error",
              subtitle: "Failed to move item",
              description: "Please try again later",
            },
          },
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const FolderItem = ({ folder }) => (
    <div
      key={folder.id}
      className={`flex cursor-pointer hover:bg-gray-50 items-center py-[10px] px-[17px] justify-between ${
        isFoldersLoading ? "opacity-50 pointer-events-none" : ""
      }`}
      // onDoubleClick={() => {
      //   setFolderStack((prev) => [
      //     ...prev,
      //     { id: folder.id, folderName: folder.folderName },
      //   ]);
      //   setCurrentFolderId(folder.id);
      //   loadFolders(folder.id);
      // }}
      onDoubleClick={(e) => e.preventDefault()}
      onClick={() => handleFolderClick(folder)}
    >
      <div className="flex items-center">
        {folder.phase_id && (
          <div className="mr-2.5">
            <PhaseIcon phaseId={+folder.phase_id} width="10px" height="10px" />
          </div>
        )}
        <img
          className="w-4 h-4 mr-2"
          src="/images/v2/library/folder-fill.svg"
        />
        <p className="text-gray-900 out-300-14 truncate max-w-[220px]">
          {folder.folderName}
        </p>
      </div>
      {/* {moveToFolderId === folder.id && (
        <CheckIcon className="text-primary-700" />
      )} */}
    </div>
  );

  const ProjectItem = ({ project }) => (
    <div
      key={project.id}
      className="flex cursor-pointer hover:bg-gray-50 items-center py-[10px] px-[17px] justify-between"
      // onDoubleClick={() => {
      //   setMoveToProjectId(project.id);
      //   setViewMode("folders");
      //   setFolderStack([]);
      //   setCurrentFolderId(null);
      //   setMoveToFolderId(null);
      // }}
      onDoubleClick={(e) => e.preventDefault()}
      onClick={() => {
        setMoveToProjectId(project.id);
        setViewMode("folders");
        setFolderStack([]);
        setCurrentFolderId(null);
        setMoveToFolderId(null);
        loadFolders(null);
      }}
    >
      <div className="flex items-center">
        <CustomAvatar
          size={20}
          title={project.title}
          whiteText
          src={project.projectImage}
          color={project.colorCode}
          className="mr-2"
        />
        <p className="text-gray-900 out-300-14 truncate max-w-[220px]">
          {project.title}
        </p>
      </div>
      {/* {moveToProjectId === project.id && (
        <CheckIcon className="text-primary-700" />
      )} */}
    </div>
  );

  const renderBreadcrumbs = () => {
    return (
      <div className="flex items-center flex-wrap text-sm text-gray-500 my-2">
        <span
          className="cursor-pointer out-300-14 text-gray-500"
          onClick={() => {
            setFolderStack([]);
            setMoveToProjectId(null);
            setCurrentFolderId(null);
            setViewMode("projects");
          }}
        >
          Library
        </span>

        {/* show project name or ... if we have folders */}
        {moveToProjectId && (
          <>
            <span className="mx-1">/</span>
            {folderStack?.length > 0 ? (
              <Dropdown
                open={breadcrumbDropdownVisible}
                onOpenChange={(visible) =>
                  setBreadcrumbDropdownVisible(visible)
                }
                placement="bottomLeft"
                overlay={
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg h-[48px] min-w-[200px] flex justify-start items-center hover:bg-gray-50 cursor-pointer">
                    <div
                      className="px-4 py-2 cursor-pointer"
                      onClick={() => {
                        setFolderStack([]);
                        setCurrentFolderId(null);
                        loadFolders(null);
                        setBreadcrumbDropdownVisible(false);
                      }}
                    >
                      <div className="flex items-center">
                        <span className="text-gray-700 out-300-14">
                          {projectList.find((p) => p.id === moveToProjectId)
                            ?.title || "Selected Project"}
                        </span>
                      </div>
                    </div>
                  </div>
                }
                trigger={["click"]}
              >
                <span className="cursor-pointer font-medium text-gray-700">
                  ...
                </span>
              </Dropdown>
            ) : (
              <span
                className="cursor-pointer font-medium text-black"
                onClick={() => {
                  setFolderStack([]);
                  setCurrentFolderId(null);
                  loadFolders(null);
                }}
              >
                {projectList.find((p) => p.id === moveToProjectId)?.title ||
                  "Selected Project"}
              </span>
            )}
          </>
        )}

        {folderStack.map((crumb, idx) => (
          <React.Fragment key={crumb.id || idx}>
            <span className="mx-1">/</span>
            {idx === folderStack?.length - 1 ? (
              <span className="cursor-pointer font-medium text-black">
                {crumb.folderName}
              </span>
            ) : (
              <Dropdown
                placement="bottomLeft"
                overlay={
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg h-[48px] min-w-[200px]  flex justify-start items-center hover:bg-gray-50 cursor-pointer">
                    <div
                      className="px-4 py-2 w-full cursor-pointer"
                      onClick={() => {
                        const newStack = folderStack.slice(0, idx + 1);
                        setFolderStack(newStack);
                        setCurrentFolderId(crumb.id);
                        loadFolders(crumb.id);
                        setMoveToFolderId(crumb.id);
                      }}
                    >
                      <div className="flex items-center gap-x-2">
                        <span className="text-gray-700 out-300-14">
                          {crumb.folderName}
                        </span>
                      </div>
                    </div>
                  </div>
                }
                trigger={["click"]}
              >
                <span className="cursor-pointer font-medium text-gray-700">
                  ...
                </span>
              </Dropdown>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };
  const renderContent = () => {
    if (viewMode === "projects") {
      return (
        <>
          {projectList
            .filter((p) => p.title.toLowerCase().includes(search.toLowerCase()))
            .map((project) => (
              <ProjectItem key={project.id} project={project} />
            ))}

          {projectList.length === 0 && (
            <div className="py-4 text-center text-gray-500 out-300-14">
              No projects found
            </div>
          )}
        </>
      );
    } else {
      // we r in a project context, show folders
      return (
        <>
          {/* {(projectId || moveToProjectId) && (
            <div
              onClick={() => setMoveToFolderId(null)}
              className="flex cursor-pointer hover:bg-gray-50 items-center py-[10px] px-[17px]"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center">
                  <img
                    className="w-4 h-4 mr-2"
                    src="/images/v2/library/folder-fill.svg"
                  />
                  <p className="text-gray-900 out-300-14">Root Folder</p>
                </div>
                {moveToFolderId === null && (
                  <CheckIcon className="text-primary-700" />
                )}
              </div>
            </div>
          )} */}

          {isFoldersLoading ? (
            <div className="py-8 text-center">
              <Spin size="small" />
              <div className="mt-2 text-gray-500 out-300-14">
                Loading folders...
              </div>
            </div>
          ) : (
            <>
              {currentFolders
                .filter((f) =>
                  f.folderName.toLowerCase().includes(search.toLowerCase())
                )
                .map((folder) => (
                  <FolderItem key={folder.id} folder={folder} />
                ))}

              {currentFolders.length === 0 && !isFoldersLoading && (
                <div className="py-4 text-center text-gray-500 out-300-14">
                  No folders found
                </div>
              )}
            </>
          )}
        </>
      );
    }
  };

  return (
    <>
      {
        <CustomModal
          visible={visible && !confirmVisible}
          width="362px"
          onCancel={onClose}
          body={
            <div className="p-[16px] h-[500px] relative">
              <p className="text-black out-500-14 pb-[16px]">Move to</p>

              <CustomInputBox
                placeholder={
                  viewMode === "projects"
                    ? "Search in library"
                    : folderStack.length > 0
                    ? `Search in ${
                        folderStack[folderStack.length - 1].folderName
                      }`
                    : moveToProjectId || projectId
                    ? `Search in ${
                        projectList.find(
                          (p) => p.id === (moveToProjectId || projectId)
                        )?.title || getCurrentProjectName()
                      }`
                    : "Search in folders"
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {renderBreadcrumbs()}

              <div className="max-h-[320px] mt-4 min-h-[200px] overflow-y-auto">
                {renderContent()}
              </div>

              <div className="absolute bottom-[16px]  right-0 px-[16px]">
                <div>
                  <button
                    className="h-[30px] px-[10px] out-500-14 bg-transparent text-gray-500 out-500-14"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    className="h-[30px] px-[10px] text-white out-500-14 bg-primary-600 rounded-[4px] ml-[13px]"
                    onClick={() => {
                      const isRootOfSameProj =
                        viewMode === "folders" &&
                        moveToFolderId === null &&
                        (moveToProjectId === null ||
                          moveToProjectId === projectId);

                      if (isRootOfSameProj) {
                        onsubmit();
                        onClose();
                      } else {
                        setConfirmVisible(true);
                      }
                    }}
                    disabled={
                      isLoading || (viewMode === "projects" && !moveToProjectId)
                    }
                  >
                    Move here
                  </button>
                </div>
              </div>
            </div>
          }
        />
      }
      {confirmVisible && (
        <ConfirmMoveModal
          onCancel={() => {
            setConfirmVisible(false);
          }}
          onConfirm={() => {
            setConfirmVisible(false);
            onsubmit();
            onClose();
          }}
          targetName={targetName}
          visible={confirmVisible}
        />
      )}
    </>
  );
};

export default MoveToModal;
